import React, { useCallback, useState } from "react";

// region External libraries
import { useQuery } from "@apollo/client";
import { Button } from "@npm-telluria-tecnologia/telluria-ui";
// endregion External libraries
// region Hooks
import { useCommandMutation } from "@hooks/useCommandMutation";
// endregion Hooks
// region Languages
import useTranslation from "@languages/useTranslation";
import { AnnouncementMessages, GlobalMessages } from "@languages/interfaces";
// endregion Languages
// region Libraries
import { Autocomplete, IconButton, TextField } from "@libraries/mui/components";
import { Delete } from "@libraries/mui/icons";
// endregion Libraries
// region Atoms
import StateMessage from "@atoms/StateMessage";
// endregion Atoms
// region Molecules
import ModalFormDialog from "@molecules/ModalFormDialog";
// endregion Molecules
// region Store
import {
  AnnouncementManagerCandidate,
  announcementManagerCandidatesQuery,
  announcementManagersQuery,
  TAnnouncementManagerCandidatesQuery,
  TAnnouncementManagersQuery
} from "src/store/graphql/groups/announcement/query.graphql";
import {
  grantAnnouncementManagerMutation,
  revokeAnnouncementManagerMutation,
  TGrantAnnouncementManagerMutation,
  TRevokeAnnouncementManagerMutation
} from "src/store/graphql/groups/announcement/mutation.graphql";
// endregion Store
// region Styles
import * as Styled from "./styles";
// endregion Styles

// region Interfaces
interface AnnouncementManagersDialogProps {
  open: boolean;
  onClose: () => void;
}
// endregion Interfaces

// region Constants
const pickGrant = (data: TGrantAnnouncementManagerMutation) => data.announcement.grantAnnouncementManager;
const pickRevoke = (data: TRevokeAnnouncementManagerMutation) => data.announcement.revokeAnnouncementManager;
// endregion Constants

/**
 * "Manage access" dialog of the announcements area: lists the staff users
 * holding ANNOUNCEMENTS_MANAGE (with grant audit info) and grants/revokes the
 * permission. Candidates (admin users without the permission) come from the
 * backend, resolved across tenants; the server enforces every rule,
 * including that a manager cannot revoke their own grant.
 */
const AnnouncementManagersDialog: React.FC<AnnouncementManagersDialogProps> = ({ open, onClose }) => {

  // region Hooks
  const { t } = useTranslation();
  // endregion Hooks

  // region States
  const [selectedCandidate, setSelectedCandidate] = useState<AnnouncementManagerCandidate | null>(null);
  // endregion States

  // region Queries
  const {
    data: managersData, loading, refetch: refetchManagers
  } = useQuery<TAnnouncementManagersQuery>(announcementManagersQuery, {
    skip: !open,
    fetchPolicy: "network-only"
  });

  const {
    data: candidatesData, refetch: refetchCandidates
  } = useQuery<TAnnouncementManagerCandidatesQuery>(announcementManagerCandidatesQuery, {
    skip: !open,
    fetchPolicy: "network-only"
  });
  // endregion Queries

  // region Mutations
  const refreshLists = useCallback(async () => {
    await Promise.all([refetchManagers(), refetchCandidates()]);
  }, [refetchManagers, refetchCandidates]);

  const grant = useCommandMutation<TGrantAnnouncementManagerMutation>(grantAnnouncementManagerMutation, pickGrant, { onSuccess: refreshLists });
  const revoke = useCommandMutation<TRevokeAnnouncementManagerMutation>(revokeAnnouncementManagerMutation, pickRevoke, { onSuccess: refreshLists });
  // endregion Mutations

  // region Constants
  const managers = managersData?.announcement?.getAnnouncementManagers ?? [];
  const candidates = candidatesData?.announcement?.getAnnouncementManagerCandidates ?? [];
  // endregion Constants

  // region Handlers
  const handleGrant = async () => {
    if (!selectedCandidate) return;

    if (await grant.run({ userId: selectedCandidate.userId })) setSelectedCandidate(null);
  };
  // endregion Handlers

  return (
    <ModalFormDialog
      open={open}
      title={t(AnnouncementMessages.managersTitle)}
      subtitle1={t(AnnouncementMessages.managersSubtitle)}
      onClose={onClose}
    >
      <Styled.Content>
        <Styled.GrantRow>
          <Autocomplete
            options={candidates}
            value={selectedCandidate}
            onChange={(_event, value) => setSelectedCandidate(value)}
            getOptionLabel={(option) => `${option.userName} (${option.userEmail})`}
            isOptionEqualToValue={(option, value) => option.userId === value.userId}
            size="small"
            fullWidth
            renderInput={(params) => (
              <TextField {...params} label={t(AnnouncementMessages.managersGrantLabel)} size="small" />
            )}
          />
          <Button
            text={t(AnnouncementMessages.managersGrantButton)}
            type="button"
            className="default-submit-button"
            onClick={handleGrant}
            loading={grant.loading}
            disabled={!selectedCandidate}
          />
        </Styled.GrantRow>

        {loading && <StateMessage>{t(GlobalMessages.loading)}</StateMessage>}

        {!loading && managers.length === 0 && (
          <StateMessage>{t(AnnouncementMessages.managersEmpty)}</StateMessage>
        )}

        {!loading && managers.map((manager) => (
          <Styled.ManagerRow key={manager.userId}>
            <Styled.ManagerInfo>
              <Styled.ManagerName>{manager.userName}</Styled.ManagerName>
              <Styled.ManagerEmail>{manager.userEmail}</Styled.ManagerEmail>
              {manager.grantedByName && (
                <Styled.GrantedBy>
                  {t(AnnouncementMessages.managersGrantedBy, { name: manager.grantedByName })}
                </Styled.GrantedBy>
              )}
            </Styled.ManagerInfo>
            <IconButton
              aria-label={t(AnnouncementMessages.managersRevoke)}
              title={t(AnnouncementMessages.managersRevoke)}
              size="small"
              disabled={revoke.loading}
              onClick={() => revoke.run({ userId: manager.userId })}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Styled.ManagerRow>
        ))}
      </Styled.Content>
    </ModalFormDialog>
  );
};

export default AnnouncementManagersDialog;
